import { useCallback, useSyncExternalStore } from 'react'

/**
 * useTheme — tema claro por defecto + oscuro conmutable para la cabina.
 *
 * El tema vive en `data-theme` sobre `<html>` (lo consumen los tokens OKLCH de
 * `cabina.css`) y, ADEMÁS, en un store externo compartido por TODOS los
 * consumidores. Esto es clave: antes el hook tenía un `useState` POR INSTANCIA,
 * así que al conmutar el tema desde la topbar los componentes que también leen
 * `theme` por JS (los mapas MapLibre, que eligen estilo claro/oscuro) NO se
 * re-renderizaban y el mapa se quedaba con el estilo viejo hasta refrescar
 * (bug T-025). Con un store compartido (`useSyncExternalStore`) un único cambio
 * notifica a todos los suscriptores y el `mapStyle` de react-map-gl reacciona.
 *
 * Orden de resolución del valor inicial:
 *   1. `?theme=dark|light` en la URL (deep-link; útil para captura de fidelidad).
 *   2. `localStorage['mapeodata-theme']` (preferencia persistida).
 *   3. 'light' por defecto.
 */
export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'mapeodata-theme'

function resolveInitial(): Theme {
  if (typeof window === 'undefined') return 'light'
  const param = new URLSearchParams(window.location.search).get('theme')
  if (param === 'dark' || param === 'light') return param
  const saved = window.localStorage.getItem(STORAGE_KEY)
  if (saved === 'dark' || saved === 'light') return saved
  return 'light'
}

// ---- Store externo compartido (un único valor para toda la app) ----
let current: Theme = resolveInitial()
const listeners = new Set<() => void>()

function applyDom(theme: Theme): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme)
  }
}

// Aplica el tema inicial al <html> al cargar el módulo (antes del primer render).
applyDom(current)

function setThemeInternal(theme: Theme): void {
  if (theme === current) return
  current = theme
  applyDom(theme)
  try {
    window.localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    /* almacenamiento no disponible (modo privado): no es crítico */
  }
  listeners.forEach((l) => l())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): Theme {
  return current
}

/** Fija el tema imperativamente (raro; normalmente se usa `toggleTheme`). */
export function setTheme(theme: Theme): void {
  setThemeInternal(theme)
}

export function useTheme(): { theme: Theme; toggleTheme: () => void } {
  const theme = useSyncExternalStore(subscribe, getSnapshot, () => 'light' as Theme)
  const toggleTheme = useCallback(() => {
    setThemeInternal(current === 'dark' ? 'light' : 'dark')
  }, [])
  return { theme, toggleTheme }
}
